import React from 'react';
import { AbsoluteFill, Sequence, useCurrentFrame, useVideoConfig } from 'remotion';
import type { Overlay, VideoSpec } from './types';
import { FONTS, onColor } from './theme';
import { pop, prog } from './anim';
import { SafeImg, useLayout } from './ui';

/** Image showing only `crop` of the source, at `width` px wide. */
const Cropped: React.FC<{ o: Overlay; width: number; shadow?: string }> = ({ o, width, shadow }) => {
    const [t, l, b, r] = o.crop ?? [0, 0, 1, 1];
    const aspect = o.aspect ?? 1;
    const height = width / aspect;
    const innerW = width / Math.max(0.05, r - l);
    const innerH = height / Math.max(0.05, b - t);
    return (
        <div style={{ position: 'relative', width, height, overflow: 'hidden', filter: shadow }}>
            <SafeImg src={o.imageUrl} style={{ position: 'absolute', left: -l * innerW, top: -t * innerH, width: innerW, height: innerH, maxWidth: 'none' }} />
        </div>
    );
};

/** Map a point normalized to the source frame onto the output frame (object-fit: cover). */
function toOutput(x: number, y: number, W: number, H: number, sourceAspect: number) {
    const outAspect = W / H;
    const drawnW = sourceAspect > outAspect ? H * sourceAspect : W;
    const drawnH = sourceAspect > outAspect ? H : W / sourceAspect;
    return { px: (W - drawnW) / 2 + x * drawnW, py: (H - drawnH) / 2 + y * drawnH };
}

const OverlayView: React.FC<{ o: Overlay; spec: VideoSpec; sourceAspect: number; frames: number }> = ({ o, spec, sourceAspect, frames }) => {
    const frame = useCurrentFrame();
    const { fps } = useVideoConfig();
    const { width: W, height: H, u } = useLayout();
    const t = spec.theme;
    const enter = pop(frame, fps, 0, 13);
    const leave = 1 - prog(frame, frames - 10, 10);
    const vis = Math.min(enter, leave);

    if (o.style === 'full') {
        const w = Math.min(W * 0.7, H * 0.62 * (o.aspect ?? 1));
        return (
            <AbsoluteFill style={{ background: t.background, alignItems: 'center', justifyContent: 'center', opacity: Math.min(prog(frame, 0, 8), leave), fontFamily: FONTS.sans, color: t.ink, gap: 26 * u }}>
                <div style={{ transform: `scale(${0.9 + 0.1 * enter})` }}>
                    <Cropped o={o} width={w} shadow={o.cutout ? 'drop-shadow(0 30px 40px rgba(0,0,0,.25))' : undefined} />
                </div>
                {o.title && <div style={{ fontSize: 64 * u, fontWeight: 800, letterSpacing: '-0.02em', textAlign: 'center' }}>{o.title}</div>}
                {o.price && <div style={{ padding: `${10 * u}px ${26 * u}px`, borderRadius: 99, background: t.accent, color: onColor(t.accent), fontSize: 40 * u, fontWeight: 800 }}>{o.price}</div>}
            </AbsoluteFill>
        );
    }

    const width = (o.size ?? (o.style === 'card' ? 0.3 : 0.28)) * W;
    const height = width / (o.aspect ?? 1) + (o.style === 'card' && (o.title || o.price) ? 150 * u : 0);
    // Default: right third, upper half, when vision gave no position.
    const { px, py } = toOutput(o.x ?? 0.78, o.y ?? 0.45, W, H, sourceAspect);
    const margin = 30 * u;
    const left = Math.min(Math.max(px - width / 2, margin), W - width - margin);
    const top = Math.min(Math.max(py - height / 2, margin), H - height - margin);
    const fromRight = px > W / 2;
    const slide = (1 - enter) * 120 * u * (fromRight ? 1 : -1);

    const body =
        o.style === 'card' ? (
            <div style={{ width, borderRadius: 26 * u, overflow: 'hidden', background: '#FFFFFF', boxShadow: '0 30px 60px -20px rgba(0,0,0,.45)', fontFamily: FONTS.sans }}>
                <div style={{ background: o.cutout ? '#F4F4F0' : '#FFFFFF', display: 'flex', justifyContent: 'center' }}>
                    <Cropped o={o} width={width} />
                </div>
                {(o.title || o.price) && (
                    <div style={{ padding: `${18 * u}px ${22 * u}px ${22 * u}px`, color: '#141414' }}>
                        {o.title && <div style={{ fontSize: 34 * u, fontWeight: 800, lineHeight: 1.15 }}>{o.title}</div>}
                        {o.price && <div style={{ marginTop: 10 * u, display: 'inline-block', padding: `${6 * u}px ${16 * u}px`, borderRadius: 99, background: t.accent, color: onColor(t.accent), fontSize: 28 * u, fontWeight: 800 }}>{o.price}</div>}
                    </div>
                )}
            </div>
        ) : o.cutout ? (
            <Cropped o={o} width={width} shadow="drop-shadow(0 24px 30px rgba(0,0,0,.35))" />
        ) : (
            <div style={{ borderRadius: 22 * u, overflow: 'hidden', border: `${6 * u}px solid #FFFFFF`, boxShadow: '0 30px 60px -20px rgba(0,0,0,.5)' }}>
                <Cropped o={o} width={width - 12 * u} />
            </div>
        );

    return (
        <AbsoluteFill style={{ pointerEvents: 'none' }}>
            <div style={{ position: 'absolute', left, top, opacity: vis, transform: `translateX(${slide}px) scale(${0.85 + 0.15 * enter})`, transformOrigin: fromRight ? 'right center' : 'left center' }}>{body}</div>
        </AbsoluteFill>
    );
};

/** All overlays of a clip scene, each in its own time window. */
export const Overlays: React.FC<{ overlays: Overlay[]; spec: VideoSpec; sourceAspect: number; sceneFrames: number }> = ({ overlays, spec, sourceAspect, sceneFrames }) => {
    const { fps } = useVideoConfig();
    return (
        <>
            {overlays.map((o, i) => {
                const from = Math.max(0, Math.round(o.start * fps));
                const frames = Math.min(Math.max(12, Math.round(o.duration * fps)), Math.max(1, sceneFrames - from));
                if (from >= sceneFrames) return null;
                return (
                    <Sequence key={i} from={from} durationInFrames={frames} layout="none">
                        <OverlayView o={o} spec={spec} sourceAspect={sourceAspect} frames={frames} />
                    </Sequence>
                );
            })}
        </>
    );
};
